import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { jsPDF } from "jspdf";
import { collection, doc, onSnapshot, query, where, getDocs } from 'firebase/firestore';
import { db, appId } from './firebase';
import { useAuth, AuthProvider } from './context/AuthContext';
import { AppGlobalStateContext } from './context/AppContext';

// Marketing Pages
import MarketingLayout from './components/MarketingLayout';
import Home from './pages/marketing/Home';
import Spot from './pages/marketing/Spot';
import About from './pages/marketing/About';
import Rules from './pages/marketing/Rules';
import Pricing from './pages/marketing/Pricing';
import Contact from './pages/marketing/Contact';

// App Components
import Header from './components/Header';
import MessageBox from './components/MessageBox';
import TermsEnforcementModal from './components/TermsEnforcementModal';

// App Pages
import LoginPage from './pages/LoginPage';
import FormsPage from './pages/FormsPage';
import BookingPage from './pages/BookingPage';
import ProfilePage from './pages/ProfilePage';
import WeeklySchedulePage from './pages/WeeklySchedulePage';
import BlockDatesPage from './pages/BlockDatesPage';
import PackagesPage from './pages/PackagesPage';

// SHOP PAGE IS NOW SHARED
import ShopPage from './pages/ShopPage';

// --- EXISTING BOOKING APP LOGIC ---
function BookingApp() {
    const { currentUser, userDetails, loadingAuth, updateUserProfile } = useAuth();
    const [currentPage, setCurrentPage] = useState('book');
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    
    // Global Data State
    const [pendingBookingsCount, setPendingBookingsCount] = useState(0);
    const [userApprovedBookingsCount, setUserApprovedBookingsCount] = useState(0);
    const [userPendingBookingsCount, setUserPendingBookingsCount] = useState(0);
    const [blockedDays, setBlockedDays] = useState([]);
    const [bookingsDisabled, setBookingsDisabled] = useState(false);
    const [bookings, setBookings] = useState([]);
    const [gateCode, setGateCode] = useState('');
    const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
    
    // UI State
    const [genericMessage, setGenericMessage] = useState('');
    const [genericMessageType, setGenericMessageType] = useState('info');
    const [showTermsModal, setShowTermsModal] = useState(false);
    const [termsText, setTermsText] = useState('');
    
    const [currentTime, setCurrentTime] = useState(new Date());

    useEffect(() => {
        if (!loadingAuth) {
            setIsLoggedIn(!!currentUser && !currentUser.isAnonymous);
        }
    }, [loadingAuth, currentUser]);

    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 60000); 
        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        if (!db || !userDetails) return; 

        const bookingsColRef = collection(db, 'artifacts', appId, 'public', 'data', 'bookings');
        const unsubBookings = onSnapshot(bookingsColRef, (snapshot) => {
            const fetched = snapshot.docs.map(doc => ({
                id: doc.id, ...doc.data(),
                startTime: doc.data().startTime?.toDate(),
                endTime: doc.data().endTime?.toDate()
            }));
            setBookings(fetched || []);
            setPendingBookingsCount(fetched.filter(b => b.status === 'pending').length);
        });

        const blockedRef = collection(db, 'artifacts', appId, 'public', 'data', 'blockedDays');
        const unsubBlocked = onSnapshot(blockedRef, (snapshot) => {
            const fetched = snapshot.docs.map(doc => ({
                id: doc.id, ...doc.data(),
                startTime: doc.data().startTime?.toDate(),
                endTime: doc.data().endTime?.toDate()
            }));
            setBlockedDays(fetched || []);
        });

        const settingsRef = doc(db, 'artifacts', appId, 'public', 'appSettings');
        const unsubSettings = onSnapshot(settingsRef, (docSnap) => {
            if(docSnap.exists()){
                const s = docSnap.data();
                setBookingsDisabled(s.bookingsDisabled);
                setGateCode(s.gateCode);
                setTermsText(s.termsAndConditions);
                if(s.termsVersion) {
                    const userAccepted = userDetails.termsAcceptedAt?.toDate();
                    const currentVersion = s.termsVersion.toDate();
                    if(!userAccepted || userAccepted < currentVersion) setShowTermsModal(true);
                }
            }
        });

        const reqRef = collection(db, 'artifacts', appId, 'public', 'data', 'userPackages');
        const unsubReq = onSnapshot(query(reqRef, where('status', '==', 'pending')), (snap) => {
            setPendingRequestsCount(snap.size);
        });

        return () => { unsubBookings(); unsubBlocked(); unsubSettings(); unsubReq(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userDetails]); 

    const handleAcceptTerms = async () => {
        if(!userDetails) return;
        try {
            await updateUserProfile({ termsAcceptedAt: new Date() });
            setShowTermsModal(false);
        } catch(e) { setGenericMessage("Error accepting terms."); setGenericMessageType('error'); }
    };

    const handleDownloadMyBookingsPdf = async () => {
        if (!currentUser) return;
        try {
            const q = query(collection(db, 'artifacts', appId, 'public', 'data', 'bookings'), where('userId', '==', currentUser.uid));
            const snap = await getDocs(q);
            const userBookings = snap.docs.map(d => ({...d.data(), startTime: d.data().startTime.toDate()})).filter(b => b.status === 'approved');
            
            const pdf = new jsPDF();
            pdf.setFontSize(18);
            pdf.text("My Bookings - Shep's Sniff Spot", 10, 15);
            pdf.setFontSize(12);
            
            let yPos = 30;
            userBookings.sort((a,b) => a.startTime - b.startTime).forEach((b) => {
                pdf.text(`${b.startTime.toDateString()} @ ${b.startTime.toLocaleTimeString()} - ${b.endTime.toLocaleTimeString()}`, 10, yPos);
                yPos += 10;
                if(yPos > 280) { pdf.addPage(); yPos = 20; }
            });
            pdf.save("My_Sniffspot_Bookings.pdf");
        } catch(e) { console.error(e); }
    };

    const getVisibleGateCode = () => {
        if (!gateCode || !userDetails) return null;
        if (userDetails.isGateCodeBlocked) return null; 
        if (userDetails.isAdmin) return gateCode; 

        const now = currentTime.getTime();
        const tenMinutes = 10 * 60 * 1000;

        const hasActiveBooking = bookings.some(b => {
            if (b.userId !== userDetails.uid || b.status !== 'approved') return false;
            if (!b.startTime || !b.endTime) return false;
            const start = b.startTime.getTime();
            const end = b.endTime.getTime();
            return now >= (start - tenMinutes) && now <= end;
        });

        return hasActiveBooking ? gateCode : null;
    };

    if (loadingAuth) return <div className="text-center mt-10">Loading...</div>;
    if (!isLoggedIn) return <LoginPage onLoginSuccess={() => { setIsLoggedIn(true); setCurrentPage('book'); }} />;

    return (
        <AppGlobalStateContext.Provider value={{ 
            pendingBookingsCount, userPendingBookingsCount, userApprovedBookingsCount, 
            handleDownloadMyBookingsPdf, setGenericMessage, setGenericMessageType, gateCode, pendingRequestsCount 
        }}>
            <div className="min-h-screen bg-gradient-to-br from-lime-200 via-green-100 to-blue-100 font-inter">
                <Header 
                    currentPage={currentPage} 
                    setCurrentPage={setCurrentPage} 
                    availableSpots={userDetails?.availableSpots} 
                    gateCode={getVisibleGateCode()} 
                />
                <main>
                    {showTermsModal && <TermsEnforcementModal termsText={termsText} onAccept={handleAcceptTerms} />}
                    <MessageBox message={genericMessage} type={genericMessageType} onClose={() => setGenericMessage('')} />
                    
                    {currentPage === 'book' && <BookingPage setPendingBookingsCount={setPendingBookingsCount} setUserBookedSpotsCount={setUserApprovedBookingsCount} setUserPendingBookingsCount={setUserPendingBookingsCount} blockedDays={blockedDays} bookingsDisabled={bookingsDisabled} bookings={bookings} />}
                    {currentPage === 'schedule' && <WeeklySchedulePage blockedDays={blockedDays} />}
                    {currentPage === 'profile' && <ProfilePage />}
                    {currentPage === 'packages' && <PackagesPage userDetails={userDetails} />}
                    
                    {/* SHOP REMOVED FROM HERE */}
                    
                    {currentPage === 'forms' && <FormsPage />}
                    {currentPage === 'blockDates' && userDetails?.isAdmin && <BlockDatesPage bookingsDisabled={bookingsDisabled} setBookingsDisabled={setBookingsDisabled} gateCode={gateCode} blockedDays={blockedDays} bookings={bookings} />}
                </main>
            </div>
        </AppGlobalStateContext.Provider>
    );
}

// --- MAIN APP ROUTER ---
function App() {
    return (
        <AuthProvider>
            <Router>
                <Routes>
                    {/* 1. Marketing Site Routes (PUBLIC) */}
                    <Route path="/" element={<MarketingLayout />}>
                        <Route index element={<Home />} />
                        <Route path="the-spot" element={<Spot />} />
                        <Route path="shop" element={<ShopPage />} /> {/* SHOP IS HERE NOW */}
                        <Route path="about" element={<About />} />
                        <Route path="rules" element={<Rules />} />
                        <Route path="pricing" element={<Pricing />} />
                        <Route path="contact" element={<Contact />} />
                    </Route>

                    {/* 2. App Route (PRIVATE) */}
                    <Route path="/app" element={<BookingApp />} />
                    
                    {/* Catch-all */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </Router>
        </AuthProvider>
    );
}

export default App;